// B1.4: dashboard domain methods (move-only from client.ts).
import { ApiClientCore, buildQueryString } from './http/core';

export class DashboardApi extends ApiClientCore {
  // ─── Dashboard ─────────────────────────────────────────────────────────────
  async getDashboard(): Promise<any> {
    return this.get('/api/v1/dashboard');
  }
  async getWebDashboard(locationId?: number | null): Promise<any> {
    const q =
      locationId != null && locationId > 0 ? buildQueryString({ location_id: locationId }) : '';
    return this.get(`/api/v1/dashboard/web-home${q}`);
  }
  async listNotifications(opts?: {
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
    excludeChat?: boolean;
    locationIds?: number[];
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (opts?.unreadOnly) params.set('unread_only', 'true');
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.offset != null) params.set('offset', String(opts.offset));
    if (opts?.excludeChat) params.set('exclude_chat', 'true');
    opts?.locationIds?.forEach((id) => params.append('location_ids', String(id)));
    const qs = params.toString();
    return this.get(`/api/v1/notifications/${qs ? `?${qs}` : ''}`);
  }
  async getNotificationCount(opts?: { excludeChat?: boolean; locationIds?: number[] }): Promise<{ count: number }> {
    const params = new URLSearchParams();
    if (opts?.excludeChat) params.set('exclude_chat', 'true');
    opts?.locationIds?.forEach((id) => params.append('location_ids', String(id)));
    const qs = params.toString();
    return this.get(`/api/v1/notifications/count${qs ? `?${qs}` : ''}`);
  }
  async markNotificationsRead(body: { notification_ids?: number[]; exclude_chat?: boolean }): Promise<any> {
    return this.post('/api/v1/notifications/read', body);
  }
  async getOperationalDaySummary(params: {
    locationId: number;
    businessDate: string;
    include?: string;
  }): Promise<any> {
    const q: Record<string, any> = {
      location_id: params.locationId,
      business_date: params.businessDate,
    };
    if (params.include) q.include = params.include;
    return this.get(`/api/v1/operational-day/summary${buildQueryString(q)}`);
  }
  async getOperationalDayTimeline(params: {
    locationId: number;
    businessDate: string;
    offset?: number;
    limit?: number;
  }): Promise<{
    items: Array<{
      kind: string;
      id: number;
      at: string;
      title: string;
      subtitle?: string | null;
      payload?: Record<string, unknown>;
    }>;
    total: number;
    offset: number;
    limit: number;
    has_more: boolean;
  }> {
    const q: Record<string, any> = {
      location_id: params.locationId,
      business_date: params.businessDate,
      offset: params.offset ?? 0,
      limit: params.limit ?? 40,
    };
    return this.get(`/api/v1/operational-day/timeline${buildQueryString(q)}`);
  }
  async getDashboardShiftsChart(days = 30): Promise<any> {
    return this.get(`/api/v1/dashboard/charts/shifts${buildQueryString({ days })}`);
  }
  async getDashboardTasksChart(): Promise<any> {
    return this.get('/api/v1/dashboard/charts/tasks');
  }
  async getDashboardIncidentsChart(days = 30): Promise<any> {
    return this.get(`/api/v1/dashboard/charts/incidents${buildQueryString({ days })}`);
  }
  async getNotificationPreferences(): Promise<any> {
    return this.get('/api/v1/notifications/preferences');
  }
  async updateNotificationPreferences(data: Record<string, boolean>): Promise<any> {
    return this.patch('/api/v1/notifications/preferences', data);
  }
  async getTodayDashboard(locationIds?: number[]): Promise<any> {
    const qs = locationIds?.length ? '?' + locationIds.map((id) => `location_ids=${id}`).join('&') : '';
    return this.get(`/api/v1/today${qs}`);
  }
}
